import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useApiQuery } from '../../hooks/useApi';
import { useEmpresasContext } from '../../context/EmpresasContext';
import { formatDateSafe, formatDateTimeSafe } from '../../utils/dateUtils';
import { Button, Card, Badge } from '../../components/ui';
import { ReportesService, AuthService } from '../../services';
import ModalOpcionesPDF from '../../components/modales/ModalOpcionesPDF';
import ControlCombustibleReporteSection from '../../components/combustible/ControlCombustibleReporteSection';

export const ReporteDetalleMovilView: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { usuario } = useAuth();
  const { empresaActiva } = useEmpresasContext();
  const navigate = useNavigate();
  const [modalPDFOpen, setModalPDFOpen] = useState(false);

  const reporteId = id ? parseInt(id, 10) : null;

  const {
    data: reporte,
    isLoading,
    error,
    refetch: refetchReporte
  } = useApiQuery(
    ['reporte', reporteId],
    () => reporteId ? ReportesService.getReporte(reporteId) : Promise.resolve(null),
    {
      enabled: !!reporteId,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const puedeEditar = () => {
    if (!reporte || !usuario) return false;

    if (usuario.tipo_usuario === 'administrador') return true;
    if (usuario.tipo_usuario === 'supervisor') return true;
    if (usuario.tipo_usuario === 'operador') {
      return reporte.creado_por === usuario.id;
    }

    return false;
  };

  // Refrescar cuando se viene de edición
  useEffect(() => {
    const shouldInvalidate = localStorage.getItem(`invalidate-reporte-${reporteId}`);
    if (shouldInvalidate && refetchReporte) {
      refetchReporte();
      localStorage.removeItem(`invalidate-reporte-${reporteId}`);
    }
  }, [reporteId, refetchReporte]);

  // Refrescar cuando la URL cambia (navegación)
  useEffect(() => {
    if (refetchReporte) {
      refetchReporte();
    }
  }, [reporteId]);

  const handleGenerarPDF = async (opciones: {
    tipo: 'frontend' | 'mapa' | 'filtros';
    incluirMapa: boolean;
    zoom: number;
  }) => {
    if (!reporte) return;

    try {
      let url: string;

      if (opciones.tipo === 'frontend') {
        url = `/mava/api/v1/reportes/${reporte.id}/pdf?include_map=${opciones.incluirMapa}&zoom=${opciones.zoom}`;
      } else {
        url = `/mava/api/v1/reportes/${reporte.id}/pdf-map?zoom=${opciones.zoom}`;
      }

      const token = AuthService.getToken();

      if (!token) {
        throw new Error(t('errors.no_auth_token'));
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Empresa-ID': empresaActiva?.empresa?.id?.toString() || '1',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `reporte_${reporte.numero}_${new Date().toISOString().split('T')[0]}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      toast.success(`PDF ${t('reports.downloaded_pdf')} ${reporte.numero}`);
      setModalPDFOpen(false);
    } catch (error: any) {
      console.error('Error generando PDF:', error);
      toast.error(`${t('errors.pdf_error')}: ${error.message}`);
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado?.toLowerCase()) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'aprobado':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rechazado':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getEstadoMaquinaColor = (estado: string) => {
    switch (estado) {
      case 'OPERATIVA':
        return 'bg-green-100 text-green-800';
      case 'FALLA_MENOR':
        return 'bg-yellow-100 text-yellow-800';
      case 'FALLA_MAYOR':
        return 'bg-red-100 text-red-800';
      case 'MANTENIMIENTO':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('reports.loading_report')}</p>
        </div>
      </div>
    );
  }

  if (error || !reporte || !reporteId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-600 mb-4">{t('errors.load_error')}</p>
          <Button onClick={() => navigate('/reportes')}>{t('reports.back_to_list')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-40">
      {/* Header Sticky */}
      <div className="bg-white border-b border-gray-200 p-4 sticky top-0 z-10">
        <div className="flex items-center mb-2">
          <button
            onClick={() => navigate('/reportes')}
            className="mr-3 text-gray-600 hover:text-gray-900"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900">{t('reports.report_number', { number: reporte.numero })}</h1>
            <p className="text-xs text-gray-600">{formatDateSafe(reporte.fecha)}</p>
          </div>
          <Badge className={getEstadoColor(reporte.estado)}>
            {reporte.estado}
          </Badge>
        </div>
      </div>

      {/* Contenido */}
      <div className="p-4 space-y-3">
        {/* Información General */}
        <Card className="bg-white">
          <div className="p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">{t('reports.general_info')}</h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('reports.machine')}</label>
                <div className="text-sm font-semibold text-gray-900">
                  {reporte.maquina_codigo && reporte.maquina_descripcion && reporte.maquina_descripcion.trim()
                    ? `${reporte.maquina_codigo} - ${reporte.maquina_descripcion}`
                    : reporte.maquina_codigo || 'N/A'
                  }
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('reports.operator')}</label>
                <div className="text-sm font-semibold text-gray-900">
                  {reporte.operador_codigo ? `${reporte.operador_codigo} - ${reporte.operador_descripcion}` : t('reports.no_operator')}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('reports.machine_status')}</label>
                <Badge className={getEstadoMaquinaColor(reporte.estado_maquina)}>
                  {reporte.estado_maquina}
                </Badge>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('reports.created_by_label')}</label>
                <div className="text-sm text-gray-900">
                  {reporte.creador_username} - {formatDateTimeSafe(reporte.created_at)}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Horómetros */}
        <Card className="bg-white">
          <div className="p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">{t('reports.horometers')}</h3>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-xl font-bold text-blue-600">
                  {reporte.horometro_inicial}h
                </div>
                <div className="text-xs text-gray-500">{t('reports.initial')}</div>
              </div>

              <div>
                <div className="text-xl font-bold text-green-600">
                  {reporte.horometro_final}h
                </div>
                <div className="text-xs text-gray-500">{t('reports.final')}</div>
              </div>

              <div>
                <div className="text-xl font-bold text-purple-600">
                  {reporte.horas_trabajadas.toFixed(1)}h
                </div>
                <div className="text-xs text-gray-500">{t('reports.worked')}</div>
              </div>
            </div>
          </div>
        </Card>

        {/* Faenas */}
        {reporte.faenas && reporte.faenas.length > 0 && (
          <Card className="bg-white">
            <div className="p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                {t('admin.faenas')} ({reporte.faenas.length})
              </h3>

              <div className="space-y-2">
                {reporte.faenas.map((faena: any, index: number) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="font-medium text-sm text-gray-900">
                          {faena.faena_codigo}
                        </div>
                        <div className="text-xs text-gray-600">
                          {faena.faena_descripcion}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-sm text-blue-600">
                          {faena.horas}h
                        </div>
                      </div>
                    </div>

                    {(faena.area_trabajada > 0 || faena.longitud_metros > 0) && (
                      <div className="flex gap-3 text-xs text-gray-600 mt-2">
                        {faena.area_trabajada > 0 && (
                          <span>📏 {faena.area_trabajada} ha</span>
                        )}
                        {faena.longitud_metros > 0 && (
                          <span>🛣️ {(faena.longitud_metros / 1000).toFixed(2)} km</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between text-sm">
                <span className="font-medium text-gray-700">{t('common.total')} {t('reports.worked_hours')}</span>
                <span className="font-bold text-gray-900">
                  {reporte.faenas.reduce((sum: number, f: any) => sum + (f.horas || 0), 0).toFixed(1)}h
                </span>
              </div>
            </div>
          </Card>
        )}

        {/* Insumos */}
        {reporte.insumos && reporte.insumos.length > 0 && (
          <Card className="bg-white">
            <div className="p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                {t('admin.supplies')} ({reporte.insumos.length})
              </h3>

              <div className="space-y-2">
                {reporte.insumos.map((insumo: any, index: number) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-medium text-sm text-gray-900">
                          {insumo.nombre_insumo}
                        </div>
                        <div className="text-xs text-gray-600">
                          {insumo.cantidad} {insumo.unidad}
                        </div>
                      </div>
                      {insumo.costo_unitario > 0 && (
                        <div className="text-right">
                          <div className="text-xs text-gray-500">
                            ${insumo.costo_unitario.toLocaleString('es-CL')}/u
                          </div>
                          <div className="font-bold text-sm text-green-600">
                            ${(insumo.cantidad * insumo.costo_unitario).toLocaleString('es-CL')}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Items de Chequeo */}
        {reporte.items_chequeo && reporte.items_chequeo.length > 0 && (
          <Card className="bg-white">
            <div className="p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                {t('admin.checklist')} ({reporte.items_chequeo.length})
              </h3>

              <div className="space-y-2">
                {reporte.items_chequeo.map((item: any, index: number) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                    <span className="text-sm text-gray-900">{item.nombre_item}</span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      item.estado === 'OK'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {item.estado}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Observaciones */}
        {reporte.observaciones && (
          <Card className="bg-white">
            <div className="p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-2">{t('reports.observations')}</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{reporte.observaciones}</p>
            </div>
          </Card>
        )}

        {/* Control de combustible */}
        <ControlCombustibleReporteSection
          reporteId={reporte.id}
          nivelCombustibleInicio={reporte.nivel_combustible_inicio}
          nivelCombustibleFin={reporte.nivel_combustible_fin}
        />
      </div>

      {/* Footer con acciones */}
      <div className="fixed bottom-20 left-0 right-0 bg-white border-t border-gray-200 p-4 z-[9999] space-y-2 shadow-lg">
        <button
          onClick={() => setModalPDFOpen(true)}
          className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium flex items-center justify-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {t('reports.generate_pdf')}
        </button>

        {puedeEditar() && (
          <button
            onClick={() => navigate(`/reportes/${reporteId}/editar`)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('reports.edit_report')}
          </button>
        )}
      </div>

      {/* Modal Opciones PDF */}
      <ModalOpcionesPDF
        isOpen={modalPDFOpen}
        reporte={reporte}
        onClose={() => setModalPDFOpen(false)}
        onGenerar={handleGenerarPDF}
      />
    </div>
  );
};

export default ReporteDetalleMovilView;
